import { v4 as uuidv4 } from 'uuid';
import { BoxId } from './Box';
import { CollectionId } from './Collection';
import type { JSONContent } from '@tiptap/react';
import { TimeZoneDateTime } from 'utils/DateTimeUtils';
import { Media, MediaId } from './Media';
import type { Comment } from './Comment';
import type { Reaction } from './Reaction';

export enum MemoType {
  Quote = 'quote',
  Text = 'text',
  Link = 'link',
  Calendar = 'calendar',
  TaskList = 'taskList',
  Image = 'image',
  Audio = 'audio',
  Video = 'video',
  File = 'file',
  Doc = 'doc',
}

export type MemoId = string;

// Optional attribution attached to text/quote memos. On write we always send
// `type: 'auto'` and let the backend classify the content as a URL or plain
// text; the backend returns the resolved `type` ('text' | 'url') on read.
export type MemoSourceType = 'auto' | 'text' | 'url';

export interface MemoSource {
  type: MemoSourceType;
  content: string;
}

export interface Memo {
  id: MemoId;
  type: MemoType;
  boxId: BoxId;
  collectionId?: CollectionId;
  createdAt: string;
  datedAt: string;
  updatedAt?: string;
  lastActive: number;
  isPinned: boolean;
  pinnedAt?: string;
  parentId?: string | null;
  rank?: number | null;
  // Top-level comments from the `comments` expand on the memo lists
  // (replies come as `replyCount` only). Missing when fetched without it.
  comments?: Comment[];
  // Reactions from the `reactions` expand, same rule as `comments`.
  reactions?: Reaction[];
}

export interface CalendarMemo extends Memo {
  name: string;
  description?: string;
  start: TimeZoneDateTime;
  end: TimeZoneDateTime;
  isAllDay: boolean;
  isMoment: boolean;
}

export interface TextMemo extends Memo {
  content: string;
  title?: string;
  source?: MemoSource;
}

export interface QuoteMemo extends Memo {
  content: string;
  author: string;
  title?: string;
  description?: string;
  source?: MemoSource;
}

export interface LinkMemo extends Memo {
  content: string;
  title?: string;
  description?: string;
}

export interface DocMemo extends Memo {
  document: string;
  title?: string;
}

export interface TaskListMemo extends Memo {
  title?: string;
  tasks: string;
  description?: string;
}

export interface ImageMemo extends Memo {
  // Media ids sent on create; the server echoes back `images` with display URLs.
  imageIds?: MediaId[];
  images?: Media[];
  title?: string;
  description?: string;
}

export interface AudioMemo extends Memo {
  audioIds?: string[];
  audios?: Media[];
  title?: string;
  description?: string;
}

export interface VideoMemo extends Memo {
  // Media ids sent on create; the server echoes back `videos` with display URLs.
  videoIds?: MediaId[];
  videos?: Media[];
  title?: string;
  description?: string;
}

export interface FileMemo extends Memo {
  // Media ids sent on create; the server echoes back `files` with view URLs.
  fileIds?: MediaId[];
  files?: Media[];
  title?: string;
  description?: string;
}

export type Task = {
  isCompleted: boolean;
  content: string;
  id: string;
};

export type VersionedTaskList = {
  version: string;
  items: Task[];
};

export type AnyMemo =
  | TextMemo
  | QuoteMemo
  | LinkMemo
  | TaskListMemo
  | DocMemo
  | CalendarMemo
  | ImageMemo
  | AudioMemo
  | VideoMemo
  | FileMemo;

export const createEmptyTask = () => {
  return {
    id: uuidv4(),
    content: '',
    isCompleted: false,
  };
};

export const hasTitle = (memo: Memo): memo is TextMemo | QuoteMemo => {
  return 'title' in memo;
};

export const hasDescription = (
  memo: Memo,
): memo is
  | CalendarMemo
  | QuoteMemo
  | LinkMemo
  | TaskListMemo
  | ImageMemo
  | AudioMemo
  | VideoMemo
  | FileMemo => {
  return 'description' in memo;
};

export const hasSource = (memo: Memo): memo is TextMemo | QuoteMemo => {
  return 'source' in memo;
};

// True when the memo was given a date of its own, not just its creation time.
export const hasCustomDate = (memo: Memo): boolean =>
  Date.parse(memo.datedAt) !== Date.parse(memo.createdAt);

// Pinned memos first (most recently pinned on top), then everything in the
// same order as the server's feed: by date, newest first, with creation time
// breaking ties. A page loaded later never jumps ahead of the ones before it.
export const sortAndFilter = (
  memos: AnyMemo[],
  selectedCollectionId?: string,
): AnyMemo[] => {
  if (!memos) return [];

  return [...memos]
    .sort((a, b) => {
      if (b.isPinned !== a.isPinned) {
        return b.isPinned ? 1 : -1;
      }

      // Both pinned: the most recently pinned first. The server sets
      // pinnedAt whenever it pins, so a missing one only guards the type.
      if (a.isPinned && b.isPinned) {
        const pinnedDiff =
          Date.parse(b.pinnedAt ?? '') - Date.parse(a.pinnedAt ?? '');
        if (pinnedDiff) {
          return pinnedDiff;
        }
      }

      const datedDiff = Date.parse(b.datedAt) - Date.parse(a.datedAt);
      if (datedDiff) {
        return datedDiff;
      }
      return Date.parse(b.createdAt) - Date.parse(a.createdAt);
    })
    .filter((memo) =>
      selectedCollectionId ? memo.collectionId === selectedCollectionId : true,
    );
};

// The shape of an empty document: one empty paragraph. This is what a doc
// memo holds before anything is written, and what a blank editor starts from.
export const EMPTY_TIPTAP_DOCUMENT: JSONContent = {
  type: 'doc',
  content: [
    {
      type: 'paragraph',
      content: [],
    },
  ],
};

// True when a document has no text at all: no blocks, or only blocks with
// nothing in them.
export const isEmptyDocument = (
  document: JSONContent | null | undefined,
): boolean => {
  const blocks = document?.content ?? [];
  const hasText = (node: JSONContent): boolean =>
    Boolean(node.text?.trim()) || (node.content ?? []).some(hasText);
  return !blocks.some(hasText);
};

export const getParsedDocument = <T>(memo: DocMemo): T | null => {
  try {
    return JSON.parse(memo.document) as T;
  } catch {
    return EMPTY_TIPTAP_DOCUMENT as unknown as T;
  }
};

export const getParsedTasks = (memo: TaskListMemo): Task[] => {
  if (!memo?.tasks) return [];

  try {
    const parsed = JSON.parse(memo.tasks) as Partial<VersionedTaskList>;
    return parsed.items ?? [];
  } catch {
    return [];
  }
};

export const getTasksByCompletion = (
  memo: TaskListMemo,
): { completed: Task[]; open: Task[] } => {
  const tasks = getParsedTasks(memo);

  return {
    completed: tasks.filter((task) => task.isCompleted),
    open: tasks.filter((task) => !task.isCompleted),
  };
};

export type DocumentPreview = {
  // First non-empty line of text, for a one-line summary.
  firstLine?: string;
  // Total word count across the document.
  words: number;
};

// A glanceable summary of a doc memo, read from its TipTap JSON without
// mounting an editor. Mirrors `memo.documentPreview` in mindfeed-swift.
export const getDocumentPreview = (memo: DocMemo): DocumentPreview =>
  getDocumentPreviewFromContent(
    getParsedDocument<{ content?: unknown[] }>(memo),
  );

// The same summary for a document that is already parsed, e.g. the one the
// composer holds before the memo exists.
export const getDocumentPreviewFromContent = (
  document: { content?: unknown[] } | null | undefined,
): DocumentPreview => {
  const lines: string[] = [];

  const textOf = (node: unknown): string => {
    if (!node || typeof node !== 'object') return '';
    const { text, content } = node as { text?: string; content?: unknown[] };
    if (typeof text === 'string') return text;
    return (content ?? []).map(textOf).join('');
  };

  for (const block of document?.content ?? []) {
    const line = textOf(block).trim();
    if (line) lines.push(line);
  }

  const words = lines
    .join(' ')
    .split(/\s+/)
    .filter((word) => word.length > 0).length;

  return { firstLine: lines[0], words };
};
